import { InputLabel, FormHelperText, Typography, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useContext, useEffect, useState, useRef } from 'react';
import { ActivityContext } from '@/contexts/ActivityContext';
import { ActivityFormContext } from '@/contexts/ActivityFormContext';
import { format, isEqual, startOfDay, subYears } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { InstanceContext } from 'src/contexts/InstanceContext';
import { AiApi, languageKey } from '@api';
import AssistantSuggestionPopper from '@/components/assistant/AssistantSuggestionPopper';
import TimeInput from '@/components/utils/TimeInput';
import AssistantSuggestion from '@/components/assistant/AssistantSuggestion';
import { useAiUsable } from '@/utils/ai';

const DateForm = ({
  validateForm = true,
  useInstance = false,
}: {
  validateForm?: boolean;
  useInstance?: boolean;
}) => {
  const formHandler = useContext(ActivityFormContext);

  const { activity, setActivity } = useContext(ActivityContext);
  const { instance, setInstance } = useContext(InstanceContext);

  const useDate = useInstance ? instance.startDate : activity.startDate;
  const [date, setDate] = useState<Date | null>(null);

  useEffect(() => {
    setDate(isEqual(new Date(0), useDate) ? null : useDate);
  }, [useDate]);

  const { t, i18n } = useTranslation();
  const { palette } = useTheme();
  const lang = i18n.language.split('-')[0];
  const isLanguageKey = (lang: any): lang is languageKey => {
    return ['sv', 'en', 'de', 'fr', 'es', 'it', 'pl', 'nb', 'da', 'fi'].includes(lang);
  };

  const [dateError, setDateError] = useState<boolean | string>(false);
  const [timeError, setTimeError] = useState<boolean | string>(false);

  // Skapa-flödet: en aktivitet får inte läggas in i efterhand, alltså tidigast IDAG.
  // Ett klockslag som redan passerat idag är fortfarande tillåtet — spärren går på dag,
  // inte på minut. Annars kunde knappen låsa sig mitt i wizarden: tiden väljs i steg 2
  // och hinner passera medan man fyller i resten, och då hade steget blivit ogiltigt
  // av sig självt utan att användaren rört något.
  //
  // Redigering (validateForm=false) behåller det gamla spannet. En befintlig aktivitet
  // ligger ofta bakåt i tiden, och den måste gå att öppna och ändra som förut — spärren
  // gäller att lägga in något nytt i efterhand, inte att röra det som redan finns.
  const minDate = validateForm ? startOfDay(new Date()) : subYears(new Date(), 3);

  // Se utils/ai.ts: nyckel i miljön OCH samtycke, annars göms förslaget.
  const aiEnabled = useAiUsable();
  const [dateTimeSuggestion, setDateTimeSuggestion] = useState<{
    suggestionText: string;
    startDate: Date;
  } | null>(null);
  const [dateTimeLoading, setDateTimeLoading] = useState<boolean>(true);
  const [dateTimeError, setDateTimeError] = useState<boolean>(false);

  useEffect(() => {
    const fetchDateTimeSuggestions = async () => {
      setDateTimeLoading(true);
      try {
        const data = await new AiApi().activityDateTimeSuggestion(
          isLanguageKey(lang) ? lang : 'sv',
          activity.activityName,
        );
        if (data) {
          setDateTimeSuggestion({
            startDate: new Date(data.startDate),
            suggestionText: data.suggestionText,
          });
          setDateTimeLoading(false);
        } else {
          setDateTimeError(true);
        }
      } catch (error) {
        setDateTimeError(true);
      }
    };
    fetchDateTimeSuggestions();
  }, []);

  useEffect(() => {
    if (date !== null && !dateError && !timeError) {
      const recurrence = activity.recurringActivity;
      if (recurrence && recurrence.recurringTimes.length) {
        recurrence.recurringTimes[0] = format(date, 'HH:mm');
      }

      setActivity({ ...activity, startDate: date, recurringActivity: recurrence });
      validateForm && formHandler.setValid(true);
    } else if (formHandler.current.isValid) formHandler.setValid(false);
  }, [date, dateError, timeError]);

  const handleDateChange = (newDate: Date | null) => {
    if (!newDate) return;

    const today = new Date();
    const updatedDate = new Date(newDate);

    if (isSameDay(newDate, today)) {
      const roundedTime = getRoundedTime(today);
      updatedDate.setHours(roundedTime.hours, roundedTime.minutes);
    } else {
      updatedDate.setHours(12, 0);
    }

    setDate(updatedDate);
  };

  const isSameDay = (date1: Date, date2: Date): boolean => {
    return date1.toDateString() === date2.toDateString();
  };

  const getRoundedTime = (date: Date): { hours: number; minutes: number } => {
    let hours = date.getHours();
    let minutes = Math.ceil(date.getMinutes() / 5) * 5;

    if (minutes === 60) {
      minutes = 0;
      hours += 1;
    }

    return { hours, minutes };
  };

  const handleTimeChange = (newDate: Date | null) => {
    setDate(newDate);
  };

  return (
    <>
      <InputLabel htmlFor="activityDate" id="date-picker-label">
        {t('Which date')}
      </InputLabel>
      <DatePicker
        value={date}
        showDaysOutsideCurrentMonth={true}
        onChange={handleDateChange}
        onError={(err) => {
          // MUI lämnar felet som kod ('minDate', 'invalidDate', …) och koden hamnade
          // rakt i hjälptexten nedan. Med spärren ovan träffas 'minDate' i vardagen —
          // då duger inte ett kodord, det ska stå vad som är fel.
          setDateError(
            err === 'minDate' ? (t('Date cannot be in the past') as string) : err ?? false,
          );
        }}
        minDate={minDate}
        slotProps={{
          textField: {
            id: 'activityDate',
            inputProps: {
              'aria-labelledby': 'date-picker-label',
              tabIndex: 0,
            },
          },
        }}
      />
      <FormHelperText error={!!dateError}>{dateError}</FormHelperText>

      <InputLabel htmlFor="activityTime" id="time-input-label" sx={{ pt: 4 }}>
        {t('What time')}
      </InputLabel>
      <TimeInput
        value={date}
        onChange={handleTimeChange}
        id="activityTime"
        showTimeIcon={true}
        aria-label={t('What time') as string}
        openOnKeyboardFocus={false}
      />
      <FormHelperText error={!!timeError}>{timeError}</FormHelperText>

      {aiEnabled && (
        <AssistantSuggestionPopper
          anchorEl={formHandler.assistantAnchorEl ?? null}
          loading={dateTimeLoading}
          open={!!dateTimeSuggestion && !!formHandler.assistantAnchorEl}
        >
          <AssistantSuggestion
            title={t('Suggested date and time:')}
            onAccept={() => {
              if (dateTimeSuggestion) {
                setDate(dateTimeSuggestion.startDate);
              }
            }}
            loading={dateTimeLoading}
            error={dateTimeError}
          >
            <Typography variant="body2" color={palette.grey[50]}>
              {dateTimeSuggestion?.suggestionText}
            </Typography>
          </AssistantSuggestion>
        </AssistantSuggestionPopper>
      )}
    </>
  );
};

export default DateForm;
